// src/services/weeklyReportModel.js
//
// The patient-level weekly report: what a coordinator fills in, how it is kept
// as a draft, and the two documents it becomes on submit.
//
// What a report holds (decided 2026-10-08):
//   - Per study, the patients worked with, each with ONE status:
//     Pre-screen / Screening / Screen fail / Enrolled/Randomized / Follow-up
//     (a subject number is required for Enrolled/Randomized).
//   - Per study, optionally, a number of follow-ups NOT listed by name.
//   - For the whole report, optionally, a number of pre-screens NOT tied to a
//     study (coordinators do many a day).
// The typed numbers are ON TOP OF the people listed, never instead of them, so
// nobody is counted twice:
//   study followUps = patients marked Follow-up + "follow-ups not listed by name"
//   report pre-screens = patients marked Pre-screen (per study) + "not tied to a study"
//
// Per-study numbers are written into the SAME fields the old number-only form
// used (preScreens, screenings, screenFailures, enrollments, followUps), so
// Admin Reports and /statistics read them as before. The report-level
// pre-screens are `preScreens` on the report itself; those pages add them in
// via reportPeriodCounts().
//
// On submit a report becomes two documents with the same id:
//   weekly_reports/{id}          counts only (readable by site staff, as before)
//   weekly_report_patients/{id}  the patient list (admins + its coordinator only)
//
// Kept per patient: name, DOB, MRN, subject number and status, plus a link to
// the RegX record when there is one. The MRN lives only in the restricted
// weekly_report_patients list, never in the counts document.
//
// Pure and Firebase-free so it is unit-tested (weeklyReportModel.test.js).

import { nameTokens, normalizeDob, sameMrnNumber } from "./patientDuplicateMatch";
import { normalizeMrn9 } from "./mrn";

export const REPORT_FORMAT = 2;

export const REPORT_STATUSES = Object.freeze([
  {
    id: "preScreen",
    label: "Pre-screen",
    field: "preScreens",
    hint: "Checked for eligibility, before any screening visit.",
  },
  {
    id: "screening",
    label: "Screening",
    field: "screenings",
    hint: "Had a screening visit this week and has no result yet.",
  },
  {
    id: "screenFail",
    label: "Screen fail",
    field: "screenFailures",
    hint: "Was screened (this week or earlier) and did not qualify.",
  },
  {
    id: "enrolled",
    label: "Enrolled/Randomized",
    field: "enrollments",
    hint: "Was screened (this week or earlier) and is now enrolled or randomized. Needs a subject number.",
  },
  {
    id: "followUp",
    label: "Follow-up",
    field: "followUps",
    hint: "A visit for a patient who is already enrolled.",
  },
]);

const STATUS_BY_ID = Object.fromEntries(REPORT_STATUSES.map((s) => [s.id, s]));
export const statusLabel = (id) => STATUS_BY_ID[id]?.label || "";
export const isReportStatus = (id) => Object.prototype.hasOwnProperty.call(STATUS_BY_ID, id);

const str = (v) => String(v ?? "").trim();

/** A count typed in a box -> a whole number >= 0 ("" and junk are 0). */
export const countValue = (v) => {
  const n = Math.floor(Number(String(v ?? "").trim()));
  return Number.isFinite(n) && n > 0 ? n : 0;
};
// A typed box kept as typed in the draft ("" stays "", junk becomes "0").
const typedCount = (v) => (v === "" || v == null ? "" : String(countValue(v)));

/** Pre-screens not tied to a study, from a draft (`counts.preScreens`) or a saved report. */
export const preScreensNotTiedOf = (source) => countValue(source?.counts?.preScreens ?? source?.preScreensNotTied);

/**
 * For Admin Reports / Statistics: the numbers a new-format report holds at
 * report level (not inside any study). 0 for old reports.
 */
export const reportPeriodCounts = (report) =>
  report?.format === REPORT_FORMAT
    ? {
        // `preScreens` / `followUps` at report level: an interim layout used on
        // staging only (2026-10-08), still read so those test reports count.
        preScreens: countValue(report.preScreensNotTied ?? report.preScreens),
        followUps: countValue(report.followUps),
      }
    : { preScreens: 0, followUps: 0 };

/** A short random id for a patient row (draft + saved list). */
export const newEntryKey = () =>
  `p_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;

export const emptyDraft = () => ({
  version: 1,
  coordinator: "",
  segmentKey: "",
  // [{ studyDocId, studyId, name, patients: [entry], followUpCount: "" }]
  studies: [],
  pending: null, // a half-filled "Add patient" form: { studyDocId, entry, editing }
  counts: { preScreens: "" }, // pre-screens not tied to a study, as typed
});

const normalizeEntry = (e) => {
  if (!e || typeof e !== "object") return null;
  const ref =
    e.patientRef && typeof e.patientRef === "object" && str(e.patientRef.id)
      ? { source: str(e.patientRef.source), id: str(e.patientRef.id) }
      : null;
  return {
    key: str(e.key) || newEntryKey(),
    patientRef: ref,
    name: str(e.name),
    dob: str(e.dob),
    mrn: str(e.mrn),
    subjectNumber: str(e.subjectNumber),
    status: isReportStatus(e.status) ? e.status : "",
    notInRegx: ref ? false : e.notInRegx === true,
  };
};

/** Any stored draft -> a clean draft (unknown or broken parts dropped). */
export const normalizeDraft = (raw) => {
  const base = emptyDraft();
  if (!raw || typeof raw !== "object") return base;
  const studies = Array.isArray(raw.studies)
    ? raw.studies
        .filter((s) => s && str(s.studyDocId))
        .map((s) => ({
          studyDocId: str(s.studyDocId),
          studyId: str(s.studyId),
          name: str(s.name),
          patients: (Array.isArray(s.patients) ? s.patients : []).map(normalizeEntry).filter(Boolean),
          followUpCount: typedCount(s.followUpCount),
        }))
    : [];
  const pending =
    raw.pending && str(raw.pending.studyDocId) && raw.pending.entry
      ? {
          studyDocId: str(raw.pending.studyDocId),
          entry: normalizeEntry(raw.pending.entry),
          // true when the half-filled form is an edit of a row already listed
          editing: raw.pending.editing === true,
        }
      : null;
  return {
    ...base,
    coordinator: str(raw.coordinator),
    segmentKey: str(raw.segmentKey),
    studies,
    pending,
    counts: { preScreens: typedCount(raw.counts?.preScreens) },
  };
};

/** Nothing worth keeping: no study added, no half-filled patient, no pre-screen number. */
export const draftIsEmpty = (d) =>
  !d || ((d.studies || []).length === 0 && !d.pending && countValue(d.counts?.preScreens) === 0);

/**
 * Who a patient row is, for "already in this study" checks and suggestions:
 * the RegX record when linked, else name + DOB.
 */
export const entryIdentity = (e) => {
  if (e?.patientRef?.id) return `${e.patientRef.source || ""}:${e.patientRef.id}`;
  const name = nameTokens(e?.name).sort().join(" ");
  const dob = normalizeDob(e?.dob);
  return name || dob ? `manual:${name}|${dob}` : "";
};

/**
 * Problems that stop a patient being added. `others` are the study's other
 * rows (pass the list without this row when editing one).
 */
export const validateEntry = (entry, others = []) => {
  const errors = [];
  if (!entry) return ["Pick a patient."];
  if (!entry.patientRef && !entry.notInRegx) errors.push("Pick a patient, or add one who isn't in RegX.");
  if (entry.notInRegx) {
    if (!str(entry.name)) errors.push("Enter the patient's name.");
    if (!normalizeDob(entry.dob)) errors.push("Enter the patient's date of birth.");
    const m = normalizeMrn9(entry.mrn);
    if (m.issue === "missing") errors.push("Enter the patient's MRN.");
    else if (m.issue === "has letters") errors.push("The MRN should be digits only (up to 9).");
    else if (m.issue) errors.push("The MRN has more than 9 digits.");
  }
  if (!isReportStatus(entry.status)) errors.push("Choose a status.");
  if (entry.status === "enrolled" && !str(entry.subjectNumber)) {
    errors.push("Enter the subject number (required for Enrolled/Randomized).");
  }
  const id = entryIdentity(entry);
  const sameMrn = (o) => str(entry.mrn) && str(o.mrn) && sameMrnNumber(entry.mrn, o.mrn);
  if (others.some((o) => o.key !== entry.key && ((id && entryIdentity(o) === id) || sameMrn(o)))) {
    errors.push("This patient is already in this study's list.");
  }
  return errors;
};

const zeroTotals = () => ({
  ...Object.fromEntries(REPORT_STATUSES.map((s) => [s.field, 0])),
  followUpsNotListed: 0,
  patientCount: 0,
  notInRegxCount: 0,
});

/**
 * Counts for one study, in the old report's numeric fields. Pass the study
 * entry ({ patients, followUpCount }) or just a patient list.
 * followUps = patients marked Follow-up + follow-ups not listed by name.
 */
export const studyTotals = (studyOrPatients = []) => {
  const patients = Array.isArray(studyOrPatients) ? studyOrPatients : studyOrPatients?.patients || [];
  const notListed = Array.isArray(studyOrPatients) ? 0 : countValue(studyOrPatients?.followUpCount);
  const totals = zeroTotals();
  patients.forEach((p) => {
    const st = STATUS_BY_ID[p.status];
    if (st) totals[st.field] += 1;
  });
  totals.followUps += notListed;
  totals.followUpsNotListed = notListed;
  totals.patientCount = patients.length;
  totals.notInRegxCount = patients.filter((p) => p.notInRegx).length;
  return totals;
};

/** Totals across studies (the review pop-up's bottom row); pre-screens not tied to a study excluded. */
export const reportTotals = (studies = []) =>
  studies.reduce((acc, s) => {
    const t = studyTotals(s);
    Object.keys(acc).forEach((k) => {
      acc[k] += t[k] || 0;
    });
    return acc;
  }, zeroTotals());

/** The studies that will be submitted: those with a patient or a follow-up number. */
export const studiesToSubmit = (draft) =>
  (draft?.studies || []).filter((s) => (s.patients || []).length > 0 || countValue(s.followUpCount) > 0);

/** Anything to submit: a patient, a study follow-up number, or pre-screens not tied to a study. */
export const draftHasContent = (draft) => studiesToSubmit(draft).length > 0 || preScreensNotTiedOf(draft) > 0;

/**
 * The two documents for a submit (timestamps are added by the service).
 * Returns { reportDoc, patientsDoc }.
 */
export const buildReportDocs = ({ draft, segment, coordinator, coordinatorEmail, authorUid, authorEmail }) => {
  const studies = studiesToSubmit(draft);
  const common = {
    format: REPORT_FORMAT,
    // Pre-screens not tied to any study (report level).
    preScreensNotTied: preScreensNotTiedOf(draft),
    coordinator: str(coordinator),
    weekOf: segment.weekOf,
    monthId: segment.monthId,
    periodStart: segment.periodStart,
    periodEnd: segment.periodEnd,
  };
  const reportDoc = {
    ...common,
    // Same meaning as on old reports: who filed it (uid + email).
    coordinatorId: str(authorUid),
    coordinatorEmail: str(authorEmail).toLowerCase(),
    studyDocIds: studies.map((s) => s.studyDocId),
    studies: studies.map((s) => ({
      studyId: s.studyId || "",
      studyDocId: s.studyDocId,
      name: s.name || "",
      workedThisWeek: true,
      ...studyTotals(s),
    })),
  };
  const patientsDoc = {
    ...common,
    // Read access (rules): a site admin, the person who filed it, or the
    // coordinator it is for (an admin may file on their behalf).
    authorUid: str(authorUid),
    authorEmail: str(authorEmail).toLowerCase(),
    coordinatorEmail: str(coordinatorEmail).toLowerCase(),
    studies: studies.map((s) => ({
      studyDocId: s.studyDocId,
      studyId: s.studyId || "",
      name: s.name || "",
      followUpsNotListed: countValue(s.followUpCount),
      patients: (s.patients || []).map((p) => ({
        key: p.key,
        patientRef: p.patientRef ? { source: p.patientRef.source, id: p.patientRef.id } : null,
        name: p.name,
        dob: p.dob,
        mrn: p.mrn || "",
        subjectNumber: p.subjectNumber || "",
        status: p.status,
        notInRegx: p.notInRegx === true,
      })),
    })),
  };
  return { reportDoc, patientsDoc };
};
